import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDismissableNotice } from "../hooks/useDismissableNotice";
import { createLogger } from "../utils/logger";

const logger = createLogger("MobileInstallPrompt");

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const PROMPT_DISMISSED_KEY = "lotlocator:install-cta-dismissed";
const INSTALL_FLAG_KEY = "lotlocator:install-cta-installed";
const DISMISS_DURATION_SECONDS = 14 * 24 * 60 * 60;

function isMobileUserAgent() {
  if (typeof navigator === "undefined") {
    return false;
  }
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

function detectStandaloneMode() {
  if (typeof window === "undefined") {
    return false;
  }
  const media = window.matchMedia?.("(display-mode: standalone)");
  const navigatorStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(media?.matches || navigatorStandalone);
}

function detectCapacitorShell() {
  if (typeof window === "undefined") {
    return false;
  }
  const maybeCapacitor = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (!maybeCapacitor) {
    return false;
  }
  if (typeof maybeCapacitor.isNativePlatform === "function") {
    return maybeCapacitor.isNativePlatform();
  }
  return true;
}

interface PromptContent {
  title: string;
  message: string;
  action: string;
  onAction: () => void | Promise<void>;
}

function hasStoredInstallFlag() {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage?.getItem(INSTALL_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export function MobileInstallPrompt() {
  const { t } = useTranslation();
  const { hidden: isDismissed, dismiss } = useDismissableNotice({
    id: PROMPT_DISMISSED_KEY,
    durationSeconds: DISMISS_DURATION_SECONDS,
  });
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [promptMode, setPromptMode] = useState<"install" | "cap-shell" | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(() => detectStandaloneMode());
  const [isInstalled, setIsInstalled] = useState(() => detectStandaloneMode() || hasStoredInstallFlag());
  const isNativeShell = useMemo(() => detectCapacitorShell(), []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const media = window.matchMedia?.("(display-mode: standalone)");
    if (!media) {
      return undefined;
    }
    const handleChange = () => setIsStandalone(media.matches);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (isStandalone) {
      try {
        window.localStorage?.setItem(INSTALL_FLAG_KEY, "1");
      } catch {
        // Best effort; ignore storage failures.
      }
      setIsInstalled(true);
    }
  }, [isStandalone]);

  useEffect(() => {
    if (isDismissed || isStandalone || isNativeShell || isInstalled) {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      if (!isMobileUserAgent()) {
        return;
      }
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setPromptMode("install");
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, [isDismissed, isInstalled, isNativeShell, isStandalone]);

  useEffect(() => {
    if (isDismissed || isStandalone || isNativeShell || isInstalled) {
      return;
    }
    if (detectCapacitorShell()) {
      setPromptMode((current) => current ?? "cap-shell");
      setIsVisible(true);
    }
  }, [isDismissed, isInstalled, isNativeShell, isStandalone]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleAppInstalled = () => {
      setIsInstalled(true);
      try {
        window.localStorage?.setItem(INSTALL_FLAG_KEY, "1");
      } catch {
        // ignore storage failures
      }
      dismiss();
    };

    window.addEventListener("appinstalled", handleAppInstalled);
    return () => window.removeEventListener("appinstalled", handleAppInstalled);
  }, [dismiss]);

  const hidePrompt = useCallback(() => {
    setIsVisible(false);
    dismiss();
  }, [dismiss]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice.catch(() => undefined);
      if (choice?.outcome === "accepted") {
        setIsInstalled(true);
        try {
          window.localStorage?.setItem(INSTALL_FLAG_KEY, "1");
        } catch {
          // Ignore storage failures.
        }
      }
    } finally {
      setDeferredPrompt(null);
      hidePrompt();
    }
  }, [deferredPrompt, hidePrompt]);

  const handleContinueInApp = useCallback(() => {
    if (typeof window === "undefined") {
      hidePrompt();
      return;
    }

    const maybeCapacitor = (window as unknown as {
      Capacitor?: {
        App?: { openUrl?: (options: { url: string }) => Promise<void> };
      };
    }).Capacitor;

    const targetUrl = window.location.href ?? "https://app.lotlocator.ph";
    if (maybeCapacitor?.App?.openUrl) {
      void maybeCapacitor.App.openUrl({ url: targetUrl }).catch((error: unknown) => {
        logger.warn("Failed to defer to native shell", error);
      });
    }
    hidePrompt();
  }, [hidePrompt]);

  const shouldRender =
    isVisible && promptMode && !isDismissed && !isStandalone && !isNativeShell && !isInstalled;
  const isInstallMode = promptMode === "install" && deferredPrompt;
  const isCapacitorMode = promptMode === "cap-shell" && !deferredPrompt;

  const content = useMemo<PromptContent | null>(() => {
    if (isInstallMode) {
      return {
        title: t("install_prompt.title"),
        message: t("install_prompt.message"),
        action: t("install_prompt.install_button"),
        onAction: handleInstall,
      };
    }
    if (isCapacitorMode) {
      return {
        title: t("install_prompt.continue_title"),
        message: t("install_prompt.continue_message"),
        action: t("install_prompt.continue_button"),
        onAction: handleContinueInApp,
      };
    }
    return null;
  }, [handleContinueInApp, handleInstall, isCapacitorMode, isInstallMode, t]);

  if (!shouldRender || !content) {
    return null;
  }

  return (
    <aside
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-safe"
    >
      <div className="pointer-events-auto flex w-full max-w-xl flex-col gap-3 rounded-3xl border border-slate-200 bg-white/95 p-4 text-slate-900 shadow-2xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 dark:text-slate-100 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex-1">
          <p className="text-base font-semibold">{content.title}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">{content.message}</p>
        </div>
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            onClick={() => {
              void content.onAction();
            }}
          >
            {content.action}
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-slate-500 underline-offset-4 hover:underline dark:text-slate-300"
            onClick={hidePrompt}
          >
            {t("install_prompt.dismiss")}
          </button>
        </div>
      </div>
    </aside>
  );
}
